import type {
  BetaFeatureFlag,
  UserCapability,
} from '@packages/systems/core-constants';
import {type ComponentType} from 'react';
import {type Json} from '@packages/utilities/json';
import {type Type, type MetaAttributes} from '../DataTypeDefinition';
import {type FileRecordType} from '@packages/systems/core/records/FileRecord';
import type {QualifiedPluginIcon} from '../Plugins/PluginIconTypes';
import * as Result from '../Result';
import {type BindingPathMapPojo} from '@packages/systems/dynamo/binding-types';
import type {ContextualExpression} from '@packages/utilities/contextual-expression';
import type {ExpressionEditorDispatch} from './Dispatch';

/**
 * Properties that can live on the `system` of every `TypeImplementation`.
 */
type DataTypeImplementationSystem<ValueT> = {
  // Primary UI description for the input.
  label?: string;
  // If true, the label takes the entire width (eg so it sits above the settings UI instead of the left)
  isFullWidth?: boolean;
  // The "human-readable" text used to refer to this type anywhere in the UI.
  displayName?: string;
  /** Should the label be hidden or emphasized (bolded)? */
  labelVariant?: 'hidden' | 'emphasized';
  defaultValue?: ValueT;
  /**
   * Choose one of the custom binding UIs.
   *
   * 'custom' hands over the binding experience to the Type render
   * 'label' renders the binding dot next to the label (only usable with full width labels)
   */
  bindUIVariant?: 'label' | 'custom';
  /**
   * If true:
   * - Hide UI Settings (ExpresisonEditor) for this Type.
   * TODO: rename to hiddenSettings to match with hiddenProperty
   */
  private?: boolean;
  /**
   * If true:
   *  - Hide from the binding menu, including any sub-properties
   *  - Hide from the Props panel
   */
  hiddenProperty?: boolean;
  /** Whether this type can be externalized via a localization binding. */
  isLocalizable?: boolean;
};

type MetaUI = ComponentType<{
  meta: MetaAttributes | null;
  onChange: (arg1: MetaAttributes) => void;
}>;

export type CommonTypeFields = {
  renderSummary?: ComponentType<{
    expr: ContextualExpression;
  }>;
  system?: DataTypeImplementationSystem<any>;
  icon?: TypeImplementationIcon;
  render: ComponentType<{
    expr: ContextualExpression;
    dispatch: ExpressionEditorDispatch;
  }>;
  metaUI?: MetaUI;
  params?: Array<string>;
};
/**
  Underlying definitions of data types.
 */
export type CustomType<a> = CommonTypeFields & {
  parseValue?: (arg1: Json) => Result.ResultType<string, a>;
  meta?: MetaAttributes;
  formatValue?: (arg1: MetaAttributes, arg2: a) => a;
};

export type AliasType = CommonTypeFields & {
  type: Type;
};

type UniqueType = CommonTypeFields & {
  type: Type;
};

export class UniqueTypeImplementation implements TypeImplementation<ExpressionEditorDispatch> {
  readonly render: ComponentType<{
    expr: ContextualExpression;
    dispatch: ExpressionEditorDispatch;
  }>;
  readonly type: Type;
  readonly icon?: TypeImplementationIcon;
  readonly params?: Array<string>;
  readonly renderSummary?: ComponentType<{
    expr: ContextualExpression;
  }>;
  readonly system?: DataTypeImplementationSystem<any>;
  readonly metaUI?: MetaUI;

  static create(val: UniqueType) {
    return new UniqueTypeImplementation(val);
  }

  private constructor(val: UniqueType) {
    this.render = val.render;
    this.type = val.type;

    if (val.icon) {
      this.icon = val.icon;
    }

    if (val.params) {
      this.params = val.params;
    }

    if (val.renderSummary) {
      this.renderSummary = val.renderSummary;
    }

    if (val.system) {
      this.system = val.system;
    }

    if (val.metaUI) {
      this.metaUI = val.metaUI;
    }
  }
}

export class AliasTypeImplementation implements TypeImplementation<ExpressionEditorDispatch> {
  readonly render: ComponentType<{
    expr: ContextualExpression;
    dispatch: ExpressionEditorDispatch;
  }>;
  readonly type: Type;
  readonly icon?: TypeImplementationIcon;
  readonly params?: Array<string>;
  readonly renderSummary?: ComponentType<{
    expr: ContextualExpression;
  }>;
  readonly system?: DataTypeImplementationSystem<any>;
  readonly metaUI?: MetaUI;

  static create(val: AliasType) {
    return new AliasTypeImplementation(val);
  }

  private constructor(val: AliasType) {
    this.render = val.render;
    this.type = val.type;

    if (val.icon) {
      this.icon = val.icon;
    }

    if (val.params) {
      this.params = val.params;
    }

    if (val.renderSummary) {
      this.renderSummary = val.renderSummary;
    }

    if (val.system) {
      this.system = val.system;
    }

    if (val.metaUI) {
      this.metaUI = val.metaUI;
    }
  }
}

export class CustomTypeImplementation<
  a,
> implements TypeImplementation<ExpressionEditorDispatch> {
  readonly render: ComponentType<{
    expr: ContextualExpression;
    dispatch: ExpressionEditorDispatch;
  }>;
  readonly icon?: TypeImplementationIcon;
  readonly meta?: MetaAttributes;
  readonly params?: Array<string>;
  readonly parseValue?: (arg1: Json) => Result.ResultType<string, a>;
  readonly renderSummary?: ComponentType<{
    expr: ContextualExpression;
  }>;
  readonly system?: DataTypeImplementationSystem<any>;
  readonly metaUI?: MetaUI;
  readonly formatValue?: (arg1: MetaAttributes, arg2: a) => a;

  static create<a>(val: CustomType<a>) {
    return new CustomTypeImplementation<a>(val);
  }

  private constructor(val: CustomType<a>) {
    this.render = val.render;

    if (val.icon) {
      this.icon = val.icon;
    }

    if (val.meta) {
      this.meta = val.meta;
    }

    if (val.params) {
      this.params = val.params;
    }

    if (val.parseValue) {
      this.parseValue = val.parseValue;
    }

    if (val.renderSummary) {
      this.renderSummary = val.renderSummary;
    }

    if (val.system) {
      this.system = val.system;
    }

    if (val.metaUI) {
      this.metaUI = val.metaUI;
    }

    if (val.formatValue) {
      this.formatValue = val.formatValue;
    }
  }
}

interface TypeImplementation<DispatchT> {
  readonly icon?: TypeImplementationIcon;
  readonly params?: Array<string>;
  readonly renderSummary?: ComponentType<{
    expr: ContextualExpression;
  }>;
  readonly system?: DataTypeImplementationSystem<any>;
  readonly render: ComponentType<{
    expr: ContextualExpression;
    dispatch: DispatchT;
  }>;
}

export type DataTypeEnvironmentContext =
  | 'default'
  | 'override'
  | 'field-editor'
  | 'logic'
  | 'conditional-editor'
  | 'compact';

/**
 * The shape of the environment that each type render function has access to
 */
export type DataTypeEnvironment = {
  context: DataTypeEnvironmentContext;
  transaction: {
    begin: () => void;
    commit: () => void;
    abort: () => void;
  };
  asset: {
    choose: <A>(arg1: AssetChoose<A>) => void;
    abortChoose: () => void;
    get: (arg1: AssetGet) => FileRecordType | null;
    isSelecting: () => boolean;
    /**
     * When present, the Asset type UI uploads a device-selected file directly
     * to the CMS database (bypassing the Assets panel). Provided only for image
     * properties on components inside dynamic rich text.
     */
    uploadFromDevice?: (arg1: AssetUploadFromDevice) => void;
  };
  isEnabled: (arg1: PermissionsType) => boolean;
  dynBoundFieldPaths?: BindingPathMapPojo;
  uiFlags?: {
    disabled?: {
      /** Called per field; disables the fields it returns `true` for. */
      expression?: (expr: ContextualExpression) => boolean;
      binding?: boolean;
      reason: string;
    };
    hidden?: {
      expression?: (expr: ContextualExpression) => boolean;
      /**
       * Hides the binding dot on unbound fields. Does not affect
       * already-bound fields — use `disabled.binding` to prevent
       * changes to existing bindings.
       */
      binding?: boolean;
    };
  };
};

type AssetType = 'image' | 'all';

export type AssetChoose<A> = {
  type: AssetType;
  // Handler that is called when committing the asset selection
  onCommit: (arg1: A) => void;
  // Handler that is called typically when asset is being previewed or focused
  onChange?: (arg1: A) => void;
  // Handler that is called when cancelling or aborting the asset selection
  onCancel?: () => void;
};

export type AssetGet = {
  type: AssetType;
  id: string;
};

export type AssetUploadFromDevice = {
  // Handler called with the uploaded CMS file once the upload finishes
  onUploaded: (arg1: FileRecordType) => void;
  // Handler called when the upload fails or no file is selected
  onError?: () => void;
  // Called once a file is picked and the upload begins (not on picker cancel),
  // so the UI can show an in-progress state.
  onUploadStart?: () => void;
  // Which file types the picker accepts. 'image' (default) for image props;
  // 'all' for File links, which also allow documents and audio.
  accept?: AssetType;
};

export type PermissionsType =
  | {
      type: 'betaFlag';
      key: BetaFeatureFlag;
    }
  | {
      type: 'userCapability';
      key: UserCapability;
    };

// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
export type TypeImplementationIcon =
  | {
      tag: 'IconByName';
      val: QualifiedPluginIcon;
    }
  | {
      tag: 'IconFromType';
      val: Type;
    };

export const IconByName = (
  val: QualifiedPluginIcon
): TypeImplementationIcon => ({
  tag: 'IconByName',
  val,
});

export const IconFromType = (val: Type): TypeImplementationIcon => ({
  tag: 'IconFromType',
  val,
});

type TypeImplementationIconMatcher<a> = {
  IconByName: (arg1: QualifiedPluginIcon) => a;
  IconFromType: (arg1: Type) => a;
};
export function matchIcon<a>(
  arg1: TypeImplementationIconMatcher<a>
): (arg2: TypeImplementationIcon) => a;
export function matchIcon(matchers: any) {
  return (impl: any) => {
    const matcher = matchers[impl.tag];

    return matcher(impl.val);
  };
}
